import {Box, Select} from '@mantine/core';
import {useRouter} from 'next/router';
import {
  FOLLOW_COMPANY_RANKING_TERMS,
  FOLLOW_COMPANY_RANKING_TERM_LABELS,
} from '../../../../../core/entities/company/constants';
import type {FollowRankingListTermType} from '../../../../../core/entities/company/schema';
import {cn} from '../../../../../lib/classname';
import {navigateToFollowRanking} from '../../../../../shared/services/followRankingNavigation';

type Props = {
  className?: string;
  rankingTerm: FollowRankingListTermType;
  setRankingTerm: (term: FollowRankingListTermType) => void;
  inTitleSection?: boolean;
  previousGraduationYear?: number;
  isLastYearRanking?: boolean;
  setIsLastYearRanking?: (value: boolean) => void;
};

export const FollowRankingTermSelector: React.FC<Props> = ({
  className,
  rankingTerm,
  setRankingTerm,
  inTitleSection = false,
  previousGraduationYear,
  isLastYearRanking,
  setIsLastYearRanking,
}) => {
  const router = useRouter();

  // Determine the current value based on isLastYearRanking state or URL params
  const isShowingLastYear = inTitleSection
    ? isLastYearRanking
    : router.query.isLastYear === 'true';

  // Check if we're on last year page and not in title section (main ranking page)
  const isLastYearMainPage = !inTitleSection && isShowingLastYear;

  const currentValue = isLastYearMainPage
    ? FOLLOW_COMPANY_RANKING_TERMS.all // Force to show 'all' on last year main page
    : isShowingLastYear && rankingTerm === FOLLOW_COMPANY_RANKING_TERMS.all
      ? 'lastYearAll'
      : rankingTerm;

  const handleTermChange = (val: string | null) => {
    if (!val) return;

    if (val === 'lastYearAll') {
      if (inTitleSection && setIsLastYearRanking) {
        // For title section: update in real-time
        setRankingTerm(FOLLOW_COMPANY_RANKING_TERMS.all);
        setIsLastYearRanking(true);
      } else {
        // For main ranking page: navigate to last year's page
        navigateToFollowRanking(router, {
          term: FOLLOW_COMPANY_RANKING_TERMS.all,
          isLastYear: true,
        });
      }
    } else {
      const termValue = val as FollowRankingListTermType;
      if (inTitleSection && setIsLastYearRanking) {
        // For title section: update in real-time
        setRankingTerm(termValue);
        setIsLastYearRanking(false);
      } else {
        // For main ranking page: navigate to current year's data
        navigateToFollowRanking(router, {
          term: termValue,
          isLastYear: false,
        });
      }
    }
  };

  const selectorContent = (
    <Select
      variant="unstyled"
      className="w-full"
      value={currentValue}
      onChange={isLastYearMainPage ? undefined : handleTermChange}
      disabled={isLastYearMainPage}
      data={
        isLastYearMainPage
          ? [
              {
                value: FOLLOW_COMPANY_RANKING_TERMS.all,
                label: FOLLOW_COMPANY_RANKING_TERM_LABELS.all,
              },
            ]
          : [
              {
                value: FOLLOW_COMPANY_RANKING_TERMS.all,
                label: FOLLOW_COMPANY_RANKING_TERM_LABELS.all,
              },
              {
                value: FOLLOW_COMPANY_RANKING_TERMS.daily,
                label: FOLLOW_COMPANY_RANKING_TERM_LABELS.daily,
              },
              {
                value: FOLLOW_COMPANY_RANKING_TERMS.weekly,
                label: FOLLOW_COMPANY_RANKING_TERM_LABELS.weekly,
              },
              {
                value: FOLLOW_COMPANY_RANKING_TERMS.monthly,
                label: FOLLOW_COMPANY_RANKING_TERM_LABELS.monthly,
              },
              ...(previousGraduationYear
                ? [
                    {
                      value: 'lastYearAll',
                      label: `${previousGraduationYear}卒`,
                    },
                  ]
                : []),
            ]
      }
      styles={{
        input: {
          paddingLeft: '12px',
          paddingRight: '12px',
          cursor: 'pointer',
        },
      }}
    />
  );

  // For FollowRankingPage (not in title section)
  if (!inTitleSection) {
    return (
      <Box className="flex flex-row items-center gap-8">
        <Box
          className={`w-full pc:w-[112px] border-line border-solid border-[1px] pc:border-[0px] pc:shadow-card bg-white pc:py-8 pc:px-12 rounded-16 flex flex-col justify-center items-center ${cn(className)}`}
        >
          {selectorContent}
        </Box>
      </Box>
    );
  }

  // For CompanyListPage carousel (in title section)
  return (
    <Box
      className={`w-[112px] border-line border-solid border-[1px] pc:border-[0px] pc:shadow-card bg-white pc:py-8 pc:px-12 rounded-16 flex flex-col justify-center items-center ${cn(className)}`}
    >
      {selectorContent}
    </Box>
  );
};
